import { AlertTriangle, Check, Eye, EyeOff, KeyRound, LoaderCircle, RefreshCw, ShieldCheck, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import type { ConnectKrakenRequest, ConnectKrakenResponse } from "../../shared/contracts";
import { DefinitionRow } from "../components/data-display";
import { DashboardLoading, ErrorState, PageHeader } from "../components/page-state";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { useAuth } from "../hooks/use-auth";
import { useDashboard } from "../hooks/use-dashboard";
import { api, getErrorMessage } from "../lib/api";
import { formatDateTime, formatPercent, formatRelative } from "../lib/format";
import { cn } from "../lib/utils";

const requiredPermissions = [
  "Query Funds",
  "Query Open Orders & Trades",
  "Query Closed Orders & Trades",
  "Create & Modify Orders",
  "Cancel/Close Orders",
  "Access WebSocket API",
];

function CredentialForm({ onConnected }: { onConnected: (response: ConnectKrakenResponse) => void }) {
  const { session } = useAuth();
  const [credentials, setCredentials] = useState<ConnectKrakenRequest>({ apiKey: "", apiSecret: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{
    apiKey?: string;
    apiSecret?: string;
    form?: string;
  }>({});

  function changeCredential(key: keyof ConnectKrakenRequest, value: string) {
    setCredentials((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined, form: undefined }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const apiKey = credentials.apiKey.trim();
    const apiSecret = credentials.apiSecret.trim();
    const nextErrors = {
      apiKey: apiKey ? undefined : "Enter the API key shown in Kraken Pro.",
      apiSecret: apiSecret ? undefined : "Enter the private key shown once when the API key was created.",
    };
    if (nextErrors.apiKey || nextErrors.apiSecret) {
      setErrors(nextErrors);
      return;
    }
    if (!session.csrfToken) {
      setErrors({ form: "Your secure session needs to be refreshed." });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      const response = await api.connectKraken({ apiKey, apiSecret }, session.csrfToken);
      setCredentials({ apiKey: "", apiSecret: "" });
      onConnected(response);
    } catch (caught) {
      setErrors({ form: getErrorMessage(caught) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-5" onSubmit={(event) => void submit(event)} noValidate>
      <ol className="m-0 grid list-none gap-2 p-0 sm:grid-cols-3" aria-label="Kraken connection steps">
        <ConnectionStep number={1} title="Create key" detail="Trading access; withdrawals off" />
        <ConnectionStep number={2} title="Enter credentials" detail="API key and private key" current={!busy} />
        <ConnectionStep number={3} title="Verify & save" detail="Permissions, fees, encryption" current={busy} />
      </ol>
      <FieldGroup>
        <Field data-invalid={Boolean(errors.apiKey) || undefined}>
          <FieldLabel htmlFor="kraken-api-key">Kraken API key</FieldLabel>
          <Input
            id="kraken-api-key"
            value={credentials.apiKey}
            onChange={(event) => changeCredential("apiKey", event.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={Boolean(errors.apiKey)}
            aria-describedby={errors.apiKey ? "kraken-api-key-error" : undefined}
            disabled={busy}
            required
          />
          {errors.apiKey && <FieldError id="kraken-api-key-error">{errors.apiKey}</FieldError>}
        </Field>
        <Field data-invalid={Boolean(errors.apiSecret) || undefined}>
          <FieldLabel htmlFor="kraken-api-secret">Kraken private key</FieldLabel>
          <div className="relative">
            <Input
              id="kraken-api-secret"
              type={showSecret ? "text" : "password"}
              className="pr-12"
              value={credentials.apiSecret}
              onChange={(event) => changeCredential("apiSecret", event.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(errors.apiSecret)}
              aria-describedby={`kraken-api-secret-description${errors.apiSecret ? " kraken-api-secret-error" : ""}`}
              disabled={busy}
              required
            />
            <button type="button" className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-xl text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50" onClick={() => setShowSecret((value) => !value)} aria-label={showSecret ? "Hide private key" : "Show private key"} disabled={busy}>
              {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          <FieldDescription id="kraken-api-secret-description">Never enter your Kraken password. The private key is encrypted and cannot be displayed after saving.</FieldDescription>
          {errors.apiSecret && <FieldError id="kraken-api-secret-error">{errors.apiSecret}</FieldError>}
        </Field>
      </FieldGroup>
      {busy && (
        <div className="flex items-start gap-3 rounded-xl border border-positive/25 bg-positive-soft px-4 py-3" role="status" aria-live="polite">
          <LoaderCircle className="mt-0.5 size-4 shrink-0 animate-spin text-positive" aria-hidden="true" />
          <div><p className="text-sm font-semibold">Step 3 of 3: testing the connection</p><p className="mt-1 text-xs leading-5 text-muted-foreground">Checking the key, required permissions, fee tier, and encrypted storage.</p></div>
        </div>
      )}
      {errors.form && <FieldError>{errors.form}</FieldError>}
      <Button type="submit" variant="signal" size="lg" disabled={busy} className="w-full">
        {busy && <LoaderCircle data-icon="inline-start" className="animate-spin" />}
        {busy ? "Testing credentials…" : "Test credentials & save"}
      </Button>
    </form>
  );
}

function ConnectionStep({
  number,
  title,
  detail,
  current = false,
}: {
  number: number;
  title: string;
  detail: string;
  current?: boolean;
}) {
  return (
    <li
      className={cn(
        "rounded-xl border border-border bg-background/55 p-3",
        current && "border-primary bg-card shadow-[inset_0_0_0_1px_var(--primary)]",
      )}
      aria-current={current ? "step" : undefined}
    >
      <div className="flex items-center gap-2">
        <span className={cn("grid size-6 place-items-center rounded-full bg-muted text-[11px] font-bold text-muted-foreground", current && "bg-primary text-primary-foreground")}>{number}</span>
        <p className="text-xs font-bold">{title}</p>
      </div>
      <p className="mt-2 text-[11px] leading-4 text-muted-foreground">{detail}</p>
    </li>
  );
}

export function ConnectionPage() {
  const { session } = useAuth();
  const { dashboard, loading, error, refresh } = useDashboard();
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  if (loading && !dashboard) return <DashboardLoading />;
  if (!dashboard) return <ErrorState message={error ?? "Connection details could not be loaded."} onRetry={() => void refresh()} />;

  const { connection, fees } = dashboard;
  const missingSet = new Set(connection.missingPermissions);
  const forbiddenSet = new Set(connection.forbiddenPermissions);
  const grantedPermissions = connection.permissions.filter(
    (permission) => !missingSet.has(permission) && !forbiddenSet.has(permission),
  );

  async function testConnection() {
    if (!session.csrfToken) {
      toast.error("Your secure session needs to be refreshed.");
      return;
    }
    setTesting(true);
    try {
      await api.testKraken(session.csrfToken);
      await refresh();
      toast.success("Kraken connection verified.");
    } catch (caught) {
      toast.error(getErrorMessage(caught));
    } finally {
      setTesting(false);
    }
  }

  function connected() {
    setReplaceOpen(false);
    void refresh();
    toast.success("Kraken credentials verified and saved.");
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="SECURE EXCHANGE LINK"
        title={connection.connected ? "Connected to Kraken Pro." : "Connect Kraken Pro."}
        description={connection.connected ? "Review credential health, detected permissions, and authenticated account fees." : "Paste your API key and private key once. Hyper Profits will test them and store the credentials securely."}
        actions={
          connection.connected && <Button variant="outline" size="sm" onClick={() => void testConnection()} disabled={testing}>{testing ? <LoaderCircle data-icon="inline-start" className="animate-spin" /> : <RefreshCw data-icon="inline-start" />} Test connection</Button>
        }
      />

      {!connection.connected ? (
        <section className="grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(20rem,0.55fr)]" aria-labelledby="credentials-heading">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-7">
            <div className="mb-6 flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-accent-foreground"><KeyRound className="size-4" /></span>
              <div><h2 id="credentials-heading" className="font-display text-xl font-bold tracking-[-0.03em]">Kraken API credentials</h2><p className="mt-1 text-sm leading-6 text-muted-foreground">Trading access only. Withdrawal permission must remain off.</p></div>
            </div>
            <CredentialForm onConnected={connected} />
          </div>
          <PermissionGuide />
        </section>
      ) : (
        <>
          <section className="grid gap-7 border-b border-border pb-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.55fr)]" aria-labelledby="health-heading">
            <div>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className={cn(
                    "grid size-11 shrink-0 place-items-center rounded-full",
                    connection.privateKeyStored ? "bg-positive-soft text-positive" : "bg-coral-soft text-destructive",
                  )}>
                    {connection.privateKeyStored ? <ShieldCheck className="size-5" /> : <AlertTriangle className="size-5" />}
                  </span>
                  <div><p className="text-xs font-bold text-muted-foreground">CONNECTION HEALTH</p><h2 id="health-heading" className="font-display mt-1 text-2xl font-bold tracking-[-0.035em]">{connection.message}</h2></div>
                </div>
                <Badge variant={connection.privateKeyStored ? "positive" : "warning"}>
                  <span className={cn("size-1.5 rounded-full", connection.privateKeyStored ? "bg-positive" : "bg-destructive")} />
                  {connection.privateKeyStored ? "Connected" : "Storage warning"}
                </Badge>
              </div>
              <p className="mt-5 max-w-2xl text-sm leading-6 text-muted-foreground">
                {connection.privateKeyStored
                  ? "Private key stored encrypted. For security, it cannot be displayed; replace the credentials whenever you rotate the key in Kraken Pro."
                  : "Encrypted private-key storage is not confirmed. Live trading must remain disabled until credentials are tested and stored again."}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setReplaceOpen(true)}>Replace credentials</Button>
                <Button variant="ghost" onClick={() => void testConnection()} disabled={testing}>{testing ? <LoaderCircle data-icon="inline-start" className="animate-spin" /> : <RefreshCw data-icon="inline-start" />} Test now</Button>
              </div>
            </div>
            <dl className="rounded-2xl border border-border bg-card px-5 py-2">
              <DefinitionRow label="Key name" value={connection.keyName ?? "Not provided"} />
              <DefinitionRow label="Key hint" value={connection.keyHint ?? "Hidden"} />
              <DefinitionRow label="Last checked" value={formatRelative(connection.lastCheckedAt)} />
              <DefinitionRow label="Valid until" value={formatDateTime(connection.validUntil)} />
              <DefinitionRow label="IP allowlist" value={connection.ipAllowlistDetected ? "Detected" : "Not detected"} />
              <DefinitionRow label="Credential storage" value={connection.privateKeyStored ? "Encrypted" : "Not confirmed"} />
            </dl>
          </section>

          <section className="grid gap-8 xl:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.55fr)]">
            <div>
              <p className="text-xs font-bold text-muted-foreground">DETECTED PERMISSIONS</p>
              <h2 className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Trading access review</h2>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <PermissionGroup label="Granted" permissions={grantedPermissions} tone="granted" />
                <PermissionGroup label="Missing" permissions={connection.missingPermissions} tone="missing" />
                <PermissionGroup label="Forbidden" permissions={connection.forbiddenPermissions} tone="forbidden" />
              </div>
              {(connection.missingPermissions.length > 0 || connection.forbiddenPermissions.length > 0) && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-coral/25 bg-coral-soft px-4 py-3 text-sm leading-6 text-destructive" role="alert">
                  <AlertTriangle className="mt-1 size-4 shrink-0" /><p>Update this key in Kraken Pro before enabling live trading. Missing or forbidden permissions make the connection unsafe for automation.</p>
                </div>
              )}
            </div>
            <aside>
              <p className="text-xs font-bold text-muted-foreground">AUTHENTICATED FEES</p>
              <h2 className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Current account tier</h2>
              <dl className="mt-4 rounded-2xl border border-border bg-card px-5 py-2">
                <DefinitionRow label="Maker" value={formatPercent(fees.makerPercent)} emphasis />
                <DefinitionRow label="Taker" value={formatPercent(fees.takerPercent)} emphasis />
                <DefinitionRow label="Source" value={fees.source === "authenticated" ? "Kraken account" : "Unavailable"} />
                <DefinitionRow label="Checked" value={formatRelative(fees.updatedAt)} />
              </dl>
            </aside>
          </section>
        </>
      )}

      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Replace Kraken credentials</DialogTitle><DialogDescription>The existing private key will be replaced after the new key passes the connection and permission checks.</DialogDescription></DialogHeader>
          <CredentialForm onConnected={connected} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionGuide() {
  return (
    <aside>
      <p className="text-xs font-bold text-muted-foreground">KRAKEN KEY CHECKLIST</p>
      <h2 className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Required trading access</h2>
      <ul className="m-0 mt-4 grid list-none gap-2 p-0">
        {requiredPermissions.map((permission) => <li key={permission} className="flex items-center gap-3 border-b border-border py-2.5 text-sm"><Check className="size-4 shrink-0 text-positive" />{permission}</li>)}
      </ul>
      <div className="mt-5 flex items-start gap-3 rounded-xl bg-muted px-4 py-3 text-sm leading-6 text-foreground"><X className="mt-1 size-4 shrink-0 text-muted-foreground" /><p><strong>Withdraw Funds: off.</strong> Hyper Profits does not need withdrawal access to trade.</p></div>
    </aside>
  );
}

function PermissionGroup({
  label,
  permissions,
  tone,
}: {
  label: string;
  permissions: string[];
  tone: "granted" | "missing" | "forbidden";
}) {
  const Icon = tone === "granted" ? Check : X;
  return (
    <section className="rounded-xl border border-border bg-card p-4" aria-label={`${label} Kraken permissions`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold">{label}</h3>
        <Badge variant={tone === "granted" ? "positive" : tone === "forbidden" ? "warning" : "secondary"}>{permissions.length}</Badge>
      </div>
      {permissions.length ? (
        <ul className="m-0 mt-3 grid list-none gap-2 p-0">
          {permissions.map((permission) => (
            <li key={permission} className={cn(
              "flex items-start gap-2 text-xs leading-5",
              tone === "granted" ? "text-foreground" : "text-destructive",
            )}>
              <Icon className={cn("mt-0.5 size-3.5 shrink-0", tone === "granted" && "text-positive")} aria-hidden="true" />
              <span>{permission}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">None detected.</p>
      )}
    </section>
  );
}
